import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { InputNumber } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ClockInOutCustomFields } from 'ontraccr-common';

import { filterSettingsRows, getClockRows } from './timeTrackingConstants';

import CompanySettingsCard from '../CompanySettingsCard';
import CheckRows from '../CheckRows';
import SettingsService from '../state/settings.service';
import TimeTrackingSelect from './TimeTrackingSelect';
import { getCustomFields } from '../../timecards/state/timecards.actions';

const AUTO_CLOCK_OUT_MIN = 1;
const AUTO_CLOCK_OUT_MAX = 24;

const clampHours = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return AUTO_CLOCK_OUT_MAX;
  return Math.min(AUTO_CLOCK_OUT_MAX, Math.max(AUTO_CLOCK_OUT_MIN, Math.round(n)));
};

const MINUTE_INCREMENT_OPTIONS = [
  {
    label: '1 minute',
    value: 1,
  },
  {
    label: '15 minutes',
    value: 15,
  },
  {
    label: '30 minutes',
    value: 30,
  },
];

const renderGetView = ({
  settings,
  saveCompanySettings,
  clockRows,
  autoClockOutBlocked,
}) => function innerRender(index) {
  const row = clockRows[index];
  if (row?.key === 'minuteIncrement') {
    return (
      <TimeTrackingSelect
        data={MINUTE_INCREMENT_OPTIONS}
        defaultValue={settings.minuteIncrement || MINUTE_INCREMENT_OPTIONS[0].value}
        onChange={(minuteIncrement) => saveCompanySettings({
          minuteIncrement,
        })}
        disabled={!settings.enableManualEntry}
        format={(value) => value.label}
        getValue={(value) => value.value}
      />
    );
  }
  if (row?.key === 'autoClockOutHours') {
    const value = settings.autoClockOutHours ?? AUTO_CLOCK_OUT_MAX;
    return (
      <InputNumber
        min={AUTO_CLOCK_OUT_MIN}
        max={AUTO_CLOCK_OUT_MAX}
        value={value}
        onChange={(next) => {
          const clamped = next == null ? AUTO_CLOCK_OUT_MAX : clampHours(next);
          saveCompanySettings({ autoClockOutHours: clamped });
        }}
        onBlur={() => {
          const clamped = clampHours(value);
          if (clamped !== value) {
            saveCompanySettings({ autoClockOutHours: clamped });
          }
        }}
        disabled={!settings.enableAutoClockOut || autoClockOutBlocked}
        style={{ marginRight: 56, minWidth: 80, textAlign: 'right' }}
      />
    );
  }
  return null;
};

export default function ClockSettings({ settings, saveCompanySettings }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const customFields = useSelector((state) => state.timecards.customFields);

  useEffect(() => {
    dispatch(getCustomFields());
  }, []);

  const autoClockOutBlocked = ClockInOutCustomFields.isClockInOutCustomFieldsActive({ customFields });

  const clockRows = useMemo(() => {
    const rows = filterSettingsRows(getClockRows(t), settings);
    if (!autoClockOutBlocked) return rows;
    return rows.map((row) => {
      if (row.key !== 'enableAutoClockOut') return row;
      return {
        ...row,
        helpText: `${row.helpText} ${ClockInOutCustomFields.AUTO_CLOCK_OUT_DISABLED_BY_CLOCK_CUSTOM_FIELDS_HELP}`,
      };
    });
  }, [t, settings, autoClockOutBlocked]);

  const handleInteractions = async (item) => {
    const { key } = item;
    const isEnabled = settings[key];

    switch (key) {
      case 'enableClocking': {
        if (isEnabled) {
          saveCompanySettings({ enableBreaking: false });
          await SettingsService.clockOutAll();
        }
        break;
      }
      case 'enableBreaking': {
        if (isEnabled) await SettingsService.endBreakAll();
        break;
      }
      default:
    }
  };

  return (
    <CompanySettingsCard
      title="Clock Settings"
    >
      <CheckRows
        data={clockRows}
        onChange={async (item) => {
          await handleInteractions(item);
          if (item.key === 'enableAutoClockOut') {
            if (autoClockOutBlocked) return;
            const next = !settings.enableAutoClockOut;
            saveCompanySettings(next
              ? { enableAutoClockOut: true, autoClockOutHours: settings.autoClockOutHours ?? AUTO_CLOCK_OUT_MAX }
              : { enableAutoClockOut: false });
          } else {
            saveCompanySettings({ [item.key]: !settings[item.key] });
          }
        }}
        isChecked={(item) => settings[item.key]}
        getView={renderGetView({
          settings,
          saveCompanySettings,
          clockRows,
          autoClockOutBlocked,
        })}
        checkBoxDisabled={(item) => {
          if (item.key === 'enableBreaking' && !settings.enableClocking) return true;
          if (item.key === 'enableAutoClockOut' && autoClockOutBlocked) return true;
          return false;
        }}
      />
    </CompanySettingsCard>
  );
}

ClockSettings.propTypes = {
  settings: PropTypes.shape({
    enableClocking: PropTypes.bool,
    enableBreaking: PropTypes.bool,
    enableManualEntry: PropTypes.bool,
    detectHourBasedConflict: PropTypes.bool,
    minuteIncrement: PropTypes.number,
    enableAutoClockOut: PropTypes.bool,
    autoClockOutHours: PropTypes.number,
  }).isRequired,
  saveCompanySettings: PropTypes.func.isRequired,
};
